import React, { useState, useRef, useEffect } from 'react';
import { Button } from './Buttons/Button';
import { ReactComponent as CloseCircle } from '../../assets/svg/close-circle.svg';
import { ReactComponent as LeftArrow } from '../../assets/svg/left_arrow.svg';
import { TextInput } from './Inputs/TextInput';
import { QuillEditor } from './QuillEditor';
import { InputValidation } from '../../models/InputValidation';
import { useAlert } from '../../hooks/useAlert';
import { sendEmailToCandidates } from '../../services/RecruiterService';
import { ResponseModel } from '../../models/ResponseModel';
import useErrorPage from '../../hooks/useErrorPage';
import { SubmitFormText } from '../../helpers/LayoutTexts';
import { useMediaQuery } from 'react-responsive';
import { validateEmail } from '../../helpers/Common';
import { ErrorIndicator } from './Inputs/ErrorIndicator';
import { ApplicationPopUpLoader } from '../Loaders/ApplicationPopUpLoader';

interface Props {
    recipientEmails: string[];
    candidateIds: string;
    jobId?: number; 
    jobTitle?: string;
    recruiterId: number;
    token: string;
    style: any;
    onClose: () => void;
    onParentAlert?: (type: 'success' | 'error', message: string) => void;
}

export type EmailModel = {
    Recipients: string[];
    Cc: string[];
    Subject: string;   
    EmailBody: string;
}

export type EmailValidation = {
    Recipients: InputValidation;
    Cc: InputValidation;
    Subject: InputValidation;
    EmailBody: InputValidation;
    [key: string]: InputValidation;
}

const initialEmailModel: EmailModel = {
    Recipients: [],
    Cc: [],
    Subject: "",
    EmailBody: ""
}

const initialEmailValidation: EmailValidation = {
    Recipients: {},
    Cc: {},
    Subject: {},
    EmailBody: {}
}

export const EmailToCandidate: React.FC<Props> = (props) => {
    const styles = props.style;
    const isMobile = useMediaQuery({ query: "(max-width: 786px)" });
    const [emailModel, setEmailModel] = useState<EmailModel>({...initialEmailModel, Recipients: props.recipientEmails});
    const [validation, setValidation] = useState<EmailValidation>(initialEmailValidation);
    const {Alert, openAlert} = useAlert();
    const [toErrorPage] = useErrorPage();
    const [showCc, setShowCc] = useState<boolean>(false);
    const [ccInput, setCcInput] = useState<string>("");
    const [attachedFile, setAttachedFile] = useState<File | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const ccInputRef = useRef<HTMLInputElement>(null);
    const MAX_CC_EMAILS = 3;
    const ccCountRef = useRef(0);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        if (showCc) {
            ccInputRef.current?.focus();
        }
    }, [showCc]);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value;
        setEmailModel((values: any) => ({ ...values, [name]: value }));
        if(validation[name] && validation[name].invalid) {
            validateInput(name, value);
        }
    }

    const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value;
        validateInput(name, value);
    }

    const handleCcInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCcInput(event.target.value);
    }

    const handleCcInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            addCcEmail();
        }
    }

    const addCcEmail = () => {
        const trimmedEmail = ccInput.trim();
        if (trimmedEmail === '') return;
        if (ccCountRef.current >= MAX_CC_EMAILS) {
            setCcInput('');
            return;
        }
        if (validateEmail(trimmedEmail)) {
            setEmailModel((prevValues) => {
                if (prevValues.Cc.includes(trimmedEmail)) {
                    return prevValues;
                }
                ccCountRef.current = ccCountRef.current + 1;
                return {...prevValues, Cc: [...prevValues.Cc, trimmedEmail]};
            });
            setCcInput('');
            setValidation((values: any) => ({...values, Cc: {valid: true, invalid: false}}));
        } else {
            setValidation((values: any) => ({...values, Cc: {valid: false, invalid: true, invalidMessage: "Invalid email address!"}}));
        }
    }

    const removeCcEmail = (emailToRemove: string) => {
        setEmailModel((values) => ({...values, Cc: values.Cc.filter(email => email !== emailToRemove)}));
        ccCountRef.current = ccCountRef.current - 1;
    };

    const validateInput = (name: string, value: string) => {
        if(name === "Subject") {
            if(value === "") {
                setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Subject is required"}}));
            }
            else {
                setValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
            }
        }
    }

    const validateForm = (): number => {
        let formValidation: EmailValidation = {
            Subject: {valid: true},
            Recipients: {valid: true},
            Cc: {valid: true},
            EmailBody: {valid: true}
        }
        if(emailModel.Subject === "") {
            formValidation.Subject = {valid: false, invalid: true, invalidMessage: "Subject is required"};
        }
        if(emailModel.Recipients.length === 0) {
            formValidation.Recipients = {valid: false, invalid: true, invalidMessage: "At least one recipient is required"};
        }
        if(ccInput.trim() !== "") {
            if(!validateEmail(ccInput.trim())) {
                formValidation.Cc = {valid: false, invalid: true, invalidMessage: "Invalid email address!"};
            }
        }
        if(emailModel.Cc.length > 0) {
            const invalidCc = emailModel.Cc.find(email => !validateEmail(email));
            if(invalidCc) {
                formValidation.Cc = {valid: false, invalid: true, invalidMessage: "Invalid email address!"};
            }
        }
        if(emailModel.EmailBody === "" || emailModel.EmailBody === "<p><br></p>") {
            formValidation.EmailBody = {valid: false, invalid: true, invalidMessage: "Email body is required"};
        }
        setValidation(formValidation);
        const validInputCount = Object.keys(formValidation).filter(key => formValidation[key].valid).length;
        return validInputCount;
    }

    const removeRecipient = (emailToRemove: string) => {
        setEmailModel((values) => ({...values, Recipients: values.Recipients.filter(email => email !== emailToRemove)}));
    };

    const handleAttachmentClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            const file = files[0];
            const maxSize = 2 * 1024 * 1024;
            if (file.size > maxSize) {
                openAlert("error", "File size exceeds 2MB limit. Please choose a smaller file.");
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
                return;
            }
            setAttachedFile(file);
        }
    }

    const removeAttachment = () => {
        setAttachedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleEmailBodyChange = (content: string) => {
        setEmailModel(values => ({...values, EmailBody: content}));
    };

    const convertQuillToEmailHTML = (html: string): string => {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        const sizeMap: { [key: string]: string } = {
            'ql-size-small': '0.75em',
            'ql-size-large': '1.5em', 
            'ql-size-huge': '2.5em'
        };
        const fontMap: { [key: string]: string } = {
            'ql-font-arial': 'Arial, sans-serif',
            'ql-font-courier': '"Courier New", Courier, monospace',
            'ql-font-comic-sans-ms': '"Comic Sans MS", cursive, sans-serif',
            'ql-font-helvetica': 'Helvetica, Arial, sans-serif',
            'ql-font-open-sans': '"Open Sans", sans-serif',
            'ql-font-tahoma': 'Tahoma, Geneva, sans-serif',
            'ql-font-verdana': 'Verdana, Geneva, sans-serif',
            'ql-font-sans-serif': 'sans-serif',
            'ql-font-serif': 'serif',
            'ql-font-monospace': 'monospace'
        };
        const alignMap: { [key: string]: string } = {
            'ql-align-center': 'center',
            'ql-align-right': 'right',
            'ql-align-justify': 'justify'
        };
        Object.entries(sizeMap).forEach(([className, fontSize]) => {
            tempDiv.querySelectorAll(`.${className}`).forEach(el => {
                (el as HTMLElement).style.fontSize = fontSize;
                el.classList.remove(className);
            });
        });
        Object.entries(fontMap).forEach(([className, fontFamily]) => {
            tempDiv.querySelectorAll(`.${className}`).forEach(el => {
                (el as HTMLElement).style.fontFamily = fontFamily;
                el.classList.remove(className);
            });
        });
        Object.entries(alignMap).forEach(([className, textAlign]) => {
            tempDiv.querySelectorAll(`.${className}`).forEach(el => {
                (el as HTMLElement).style.textAlign = textAlign;
                el.classList.remove(className);
            });
        });
        const result = tempDiv.innerHTML;
        tempDiv.remove();
        return result;
    };

    async function sendEmail(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        let validInputCount = validateForm();
        if(validInputCount === 4) {
            setLoading(true);
            const emailFormData = {
                recipients: emailModel.Recipients.join(','),
                cc: emailModel.Cc.join(';'),
                subject: emailModel.Subject,
                emailBody: convertQuillToEmailHTML(emailModel.EmailBody),                                
                candidateIds: props.candidateIds,
                ...(props.jobId && { jobId: props.jobId })
            };
            const formData = new FormData();
            formData.append("ModelString", JSON.stringify(emailFormData));
            if (attachedFile) {
                formData.append('Attachment', attachedFile);
            }
            await sendEmailToCandidates(formData, props.recruiterId, props.token)
            .then((response: ResponseModel) => {
                if(response.status) {
                    if (props.onParentAlert) {
                        props.onParentAlert('success', response.message);
                    }
                    setEmailModel(initialEmailModel);
                    ccCountRef.current = 0;
                    setAttachedFile(null);
                    props.onClose();
                }
                else {
                    openAlert("error", response.message);
                }
                setLoading(false);
            })
            .catch((error) => {
                toErrorPage(error);
            });
        }
        else {
            openAlert("error", SubmitFormText.error);
        }
    }

    return (
        <div style={styles.content}>
            <div style={styles.backButton} onClick={() => props.onClose()}>
                <Button style={styles.backButton.button}><LeftArrow style={styles.backButton.icon} /></Button>
                <span style={styles.backButton.text}>{"Back"}</span>
            </div>
            {loading ? <ApplicationPopUpLoader /> : 
                <form onSubmit={sendEmail}>
                    <div>
                        <div>
                            <div style={styles.jobTitle}>{props.jobTitle}</div>
                            {!isMobile && <p style={styles.title}>{"Send a new email"}</p>}
                        </div>
                        <div style={styles.actionButtons}>
                            <Button style={styles.sendCopyButton} type="button" onClick={() => setShowCc(!showCc)}>
                                {"Send a Copy"}
                            </Button>
                            <div style={styles.verticalLine}/>
                            <Button style={styles.addAttachmentButton} type="button" onClick={handleAttachmentClick}>
                                {"Add an Attachment"}
                            </Button>
                            <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileChange}/>
                        </div>
                        <div style={styles.fieldGroup}>
                            <label style={{...styles.label, ...styles.label.recipients}}>{"Recipients (Bcc)"}</label>
                            <div style={{...styles.emailInputWrapper, ...(validation.Recipients.valid && styles.valid), ...(validation.Recipients.invalid && styles.invalid)}}>
                                <div style={styles.emailInputWrapper.content}>
                                    {emailModel.Recipients.map((email, index) => (
                                        <div key={index} style={styles.emailTag}>
                                            <span style={styles.emailTag.text}>{email}</span>
                                            <CloseCircle style={styles.emailTag.removeButton} onClick={() => removeRecipient(email)}/>
                                        </div>
                                    ))}
                                </div>
                                <div style={styles.invalidMessage}>
                                    {validation.Recipients && validation.Recipients.invalid && <ErrorIndicator invalidMessage={validation.Recipients.invalidMessage!} styles={styles.inputError}/>}
                                </div>
                            </div>
                        </div>
                        {showCc && <div style={styles.fieldGroup}>
                            <label style={styles.label}>{"Send Copy(s) to"}</label>
                            <div style={{...styles.emailInputWrapper, ...(validation.Cc.valid && styles.valid), ...(validation.Cc.invalid && styles.invalid)}}>
                                <div style={styles.emailInputWrapper.content}>
                                    {emailModel.Cc.map((email, index) => (
                                        <div key={index} style={styles.emailTag}>
                                            <span style={styles.emailTag.text}>{email}</span>
                                            <CloseCircle style={styles.emailTag.removeButton} onClick={() => removeCcEmail(email)}/>
                                        </div>
                                    ))}
                                    {emailModel.Cc.length < MAX_CC_EMAILS && <input
                                        ref={ccInputRef}
                                        type="text"
                                        value={ccInput}
                                        onChange={handleCcInputChange}
                                        onKeyDown={handleCcInputKeyDown}
                                        onBlur={addCcEmail}
                                        style={styles.emailInputWrapper.input}
                                    />}
                                </div>
                                <div style={styles.invalidMessage}>
                                    {validation.Cc && validation.Cc.invalid && <ErrorIndicator invalidMessage={validation.Cc.invalidMessage!} styles={styles.inputError}/>}
                                </div>
                            </div>
                        </div>}
                        <div style={styles.fieldGroup}>
                            <label style={styles.label}>{"Subject"}</label>
                            <TextInput
                                styles={styles.input}
                                inputType={'text'}
                                name={"Subject"}
                                value={emailModel.Subject}
                                handleChange={handleInputChange}
                                handleBlur={handleInputBlur}
                                validation={validation.Subject}
                            />
                        </div>
                        {attachedFile && (
                            <div style={styles.fieldGroup}>
                                <label style={styles.label}>{"Attachment"}</label>
                                <div style={styles.attachment}>
                                    <div style={styles.attachmentItem}>
                                        <span style={styles.attachmentName}>
                                            {attachedFile.name}
                                        </span>
                                        <CloseCircle 
                                            style={styles.attachmentRemove} 
                                            onClick={removeAttachment}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                        <div style={styles.fieldGroup}>
                            <label style={styles.label}>{"Email body"}</label>
                            <QuillEditor styles={styles.emailEditor} value={emailModel.EmailBody} onChange={handleEmailBodyChange}/>
                        </div>
                        <div style={styles.actions}>
                            <Button style={styles.sendButton} type="submit">
                                {"SEND"}
                            </Button>
                            <Button style={styles.cancelButton} type="button" onClick={() => props.onClose()}>
                                {isMobile ? "BACK" : "CLOSE"}
                            </Button>
                        </div>
                    </div>
                </form>
            }
            <Alert/>
        </div>
    );
};